import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import useGtmQuestionSet from "@/app/common-hooks/gtm/use-gtm-question-set";
import { TripDetails } from "@/app/common-types/quote/trip-details";
import selectLocationHookFlash from "@/app/common-utils/common.utils";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import {
  Individual,
  QuestionFour,
  QuestionMedicalPMI,
} from "@/config/app-constant";
import { setApiError } from "@/store/slices/errors/api-error.slice";
import { updateQuote } from "@/store/slices/quote/quote.slice";
import { RootState } from "@/store/store";
import { apiErrorMappings } from "@/utils/errors/api-error-map";

import useSavePMIDetails from "./use-save-pmi-details";
import { usePMIContext } from "../provider/private-medical-insurance.provider";

const usePMIHook = () => {
  const dispatch = useDispatch();
  const {
    setPmiDetails,
    pmiDetails,
    isTravelerEditOrOrganizerEdit,
    showOrganiserPMI,
    organiserPMI,
  } = usePMIContext();
  const { attributes } = useSelector((state: RootState) => state.quote);
  const { organiser, tripDetails } = attributes;
  const [isFooterLoading, setIsFooterLoading] = useState(false);

  const resetTrigger = useCallback(() => {
    setIsFooterLoading(false);
  }, []);

  const router = useRouter();
  const { current } = useWizard();
  const searchParams = useSearchParams();
  const allParams = searchParams.toString();
  const isEditFlow = allParams !== "";

  // Add medicalEdit check - just check if param exists
  const medicalEdit = useMemo(() => {
    return Boolean(searchParams.get("medicalEdit"));
  }, [searchParams]);

  const { data, error, loading, savePMIDetails } = useSavePMIDetails(
    resetTrigger,
    isTravelerEditOrOrganizerEdit,
    medicalEdit
  );

  useEffect(() => {
    const handleEffect = async () => {
      if (!loading && data && !error) {
        dispatch(updateQuote(data));
        router.push(current?.next ? `${current.next}/?${allParams}` : "");
      } else if (!loading && error !== null) {
        if (error.response?.status === 401) {
          router.push("/");
        } else {
          const errorObject = await apiErrorMappings(error);
          errorObject.multiButton = true;
          dispatch(setApiError(errorObject));
        }
      }
    };
    handleEffect();
  }, [data, loading, error, dispatch, router, current.next, allParams]);

  const handleOptionSelect = (arrayIndex: number, value: boolean) => {
    const updatedPmiDetails = [...pmiDetails];
    updatedPmiDetails[arrayIndex] = {
      ...updatedPmiDetails[arrayIndex],
      hasPMI:
        updatedPmiDetails[arrayIndex].hasPMI === value ? undefined : value,
    };
    setPmiDetails(updatedPmiDetails);
  };

  const hasMedicalConditions = useMemo(() => {
    return attributes.travellerDetails?.traveller?.some(
      (traveler) => traveler.medical !== undefined
    );
  }, [attributes.travellerDetails]);

  const handleBackButton = () => {
    if (tripDetails?.partyType === Individual && organiser?.travelling) {
      router.push(
        current?.edges?.organizerOnlyPrev
          ? `${current?.edges?.organizerOnlyPrev}/?${allParams}`
          : ""
      );
      return;
    }
    router.push(current?.prev ? `${current.prev}/?${allParams}` : "");
  };

  const handleContinueButton = () => {
    setIsFooterLoading(true);
    savePMIDetails(pmiDetails);
  };

  const isFilledAllTraveler = useMemo(() => {
    return (
      pmiDetails.every((item) => item.hasPMI !== undefined) &&
      (showOrganiserPMI ? organiserPMI?.hasPMI !== undefined : true)
    );
  }, [pmiDetails, showOrganiserPMI, organiserPMI]);

  const toLocationData = selectLocationHookFlash(
    attributes?.tripDetails as TripDetails
  );

  useGtmQuestionSet({
    pageTitle: QuestionMedicalPMI,
    tripType: attributes?.tripDetails?.policyType,
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
    destination: toLocationData.destination,
    destinationAdditions: toLocationData.destinationAdditions,
    privateHealthInsurance:
      pmiDetails.length > 0 ? pmiDetails[0].hasPMI : undefined,
    tripExtra: attributes?.tripDetails?.goingOnCruise
      ? QuestionFour
      : undefined,
    coverStartDate: attributes?.tripDetails?.startDate ?? undefined,
    coverEndDate: attributes?.tripDetails?.endDate ?? undefined,
    travelFrom: attributes?.tripDetails?.fromLocation ?? undefined,
    travelGroupType: attributes?.tripDetails?.partyType ?? undefined,
    isSubmitBtnClicked: data !== null,
  });

  return {
    isFilledAllTraveler,
    isFooterLoading,
    isEditFlow,
    handleOptionSelect,
    handleBackButton,
    handleContinueButton,
  };
};
export default usePMIHook;
